/**
 * paio-commerce's public menu as THIS APP's catalogue (ORDR-165).
 *
 * The framework `MenuItem` has no portions, no per-portion deltas, no nested
 * groups and no tags, so the Menu and Item screens read this instead. The
 * framework sections (`customer/publicMenu.ts`) are still built from the same
 * body, from the same single request (`readSiteMenu`).
 *
 * Contract: `serialize_item` in paio-commerce `api/public_menu.py` after
 * COMM-181 (`f1c28c0`):
 *
 *   portions:  [{code, display_names, amount_minor, default_selected: false}]   [] for one size
 *   modifiers: one group per ROOT, in order (COMM-180), each
 *              {group_id, name, min_select, max_select, free_allowance, selection_style, choices}
 *   choice:    {choice_id, name, price_delta_minor, default_selected, child_group,
 *               portion_price_deltas: {code: delta} | null}
 *
 * A PORTIONED ITEM HAS NO UNIT PRICE (`pricing_mode: VARIABLE`,
 * `unit_amount_minor: null`); its prices are its portions'. The framework
 * mapping omits it for that reason, which is why the app needs its own read.
 * An item with neither a unit price nor a portion is still omitted.
 *
 * A PORTION A CHOICE DOES NOT PRICE IS ABSENT from `portionDeltas`, never
 * zero: the cart refuses that answer at that portion.
 *
 * Availability is `publicMenu.ts`'s rule, imported, not restated: only an 86
 * is drawn unorderable, and everything else that is not OFFERED is omitted.
 */

import { chipsFor } from "../menu/chips.js";
import { describeBeyondChips } from "../menu/disclosure.js";
import { localisedName, offering, UNCATEGORISED_LABEL, UNCATEGORISED_SECTION_ID, type MenuClock } from "../customer/publicMenu.js";

export interface CatalogueChoice {
  readonly id: string;
  readonly name: string;
  readonly defaultSelected: boolean;
  /** The delta for an item sold in one size; `null` when it is keyed per portion. */
  readonly priceDeltaMinor: number | null;
  /** `{code: delta}` for a portioned item; `null` for one sold in one size. */
  readonly portionDeltas: Readonly<Record<string, number>> | null;
  readonly child: CatalogueGroup | null;
}

export interface CatalogueGroup {
  readonly id: string;
  readonly name: string;
  readonly min: number;
  readonly max: number;
  readonly freeAllowance: number;
  /** Commerce `SelectionStyle`: `SINGLE` takes exactly one answer. */
  readonly style: string;
  readonly choices: readonly CatalogueChoice[];
}

export interface CataloguePortion {
  readonly code: string;
  readonly name: string;
  readonly amountMinor: number;
}

export interface CatalogueItem {
  readonly offerRef: string;
  readonly name: string;
  /** Restricted classes and an 86's return; the allergens are in `chips`. */
  readonly description: string;
  readonly chips: readonly string[];
  readonly unitAmountMinor: number | null;
  readonly currency: string;
  readonly available: boolean;
  readonly portions: readonly CataloguePortion[];
  readonly groups: readonly CatalogueGroup[];
}

export interface CatalogueSection {
  readonly id: string;
  readonly label: string;
  readonly items: readonly CatalogueItem[];
}

function record(value: unknown): Record<string, unknown> | null {
  return typeof value === "object" && value !== null && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : null;
}

function text(value: unknown): string | null {
  return typeof value === "string" && value.trim() !== "" ? value.trim() : null;
}

function count(value: unknown): number | null {
  return Number.isInteger(value) && (value as number) >= 0 ? (value as number) : null;
}

function deltas(value: unknown): Record<string, number> | null {
  const map = record(value);
  if (map === null) {
    return null;
  }
  const out: Record<string, number> = {};
  for (const [code, delta] of Object.entries(map)) {
    if (Number.isInteger(delta)) {
      out[code] = delta as number;
    }
  }
  return out;
}

/** One served group; `null` when it is unreadable, which drops the ITEM. */
function readGroup(raw: unknown): CatalogueGroup | null {
  const group = record(raw);
  const id = text(group?.["group_id"]);
  const min = count(group?.["min_select"]);
  const max = count(group?.["max_select"]);
  const rawChoices = group?.["choices"];
  if (group === null || id === null || min === null || max === null || !Array.isArray(rawChoices)) {
    return null;
  }
  const choices: CatalogueChoice[] = [];
  for (const rawChoice of rawChoices) {
    const entry = record(rawChoice);
    const choiceId = text(entry?.["choice_id"]);
    if (entry === null || choiceId === null) {
      return null;
    }
    const child = entry["child_group"] === null || entry["child_group"] === undefined ? null : readGroup(entry["child_group"]);
    if (child === null && entry["child_group"] !== null && entry["child_group"] !== undefined) {
      return null;
    }
    const delta = entry["price_delta_minor"];
    choices.push({
      id: choiceId,
      name: text(entry["name"]) ?? choiceId,
      defaultSelected: entry["default_selected"] === true,
      priceDeltaMinor: Number.isInteger(delta) ? (delta as number) : null,
      portionDeltas: deltas(entry["portion_price_deltas"]),
      child,
    });
  }
  return {
    id,
    name: text(group["name"]) ?? "",
    min,
    max,
    freeAllowance: count(group["free_allowance"]) ?? 0,
    style: text(group["selection_style"]) ?? "MULTIPLE",
    choices,
  };
}

function readPortions(raw: unknown, language: string): CataloguePortion[] | null {
  if (raw === undefined || raw === null) {
    return [];
  }
  if (!Array.isArray(raw)) {
    return null;
  }
  const portions: CataloguePortion[] = [];
  for (const entry of raw) {
    const portion = record(entry);
    const code = text(portion?.["code"]);
    const amount = count(portion?.["amount_minor"]);
    if (portion === null || code === null || amount === null) {
      return null;
    }
    portions.push({ code, name: localisedName(portion["display_names"], language) ?? code, amountMinor: amount });
  }
  return portions;
}

interface Placed {
  readonly sectionId: string;
  readonly label: string;
  readonly order: number;
  readonly item: CatalogueItem;
}

function placeItem(raw: unknown, language: string, clock: MenuClock): Placed | null {
  const item = record(raw);
  const offerRef = text(item?.["offer_ref"]);
  const currency = text(item?.["currency"]);
  if (item === null || offerRef === null || currency === null) {
    return null;
  }
  const offered = offering(item);
  const portions = readPortions(item["portions"], language);
  const unitAmountMinor = count(item["unit_amount_minor"]);
  const rawGroups = Array.isArray(item["modifiers"]) ? item["modifiers"] : [];
  const groups = rawGroups.map(readGroup);
  if ("omitted" in offered || portions === null || (unitAmountMinor === null && portions.length === 0)) {
    return null;
  }
  if (groups.some((group) => group === null)) {
    // An item whose questions cannot be read cannot be answered, so it cannot
    // be ordered: drawing it would offer a line the cart refuses.
    return null;
  }
  const catalogueItem: CatalogueItem = {
    offerRef,
    name: localisedName(item["display_names"], language) ?? text(item["slug"]) ?? offerRef,
    description: describeBeyondChips(item, {
      language,
      now: clock.now ?? new Date(),
      ...(clock.timeZone === undefined ? {} : { timeZone: clock.timeZone }),
    }),
    chips: chipsFor(item),
    unitAmountMinor,
    currency,
    available: offered.available,
    portions,
    groups: groups as CatalogueGroup[],
  };
  const category = record(item["category"]);
  const categoryId = text(category?.["category_id"]);
  if (category === null || categoryId === null) {
    return { sectionId: UNCATEGORISED_SECTION_ID, label: UNCATEGORISED_LABEL, order: Infinity, item: catalogueItem };
  }
  const displayOrder = category["display_order"];
  return {
    sectionId: categoryId,
    label: localisedName(category["display_names"], language) ?? UNCATEGORISED_LABEL,
    order: typeof displayOrder === "number" && Number.isFinite(displayOrder) ? displayOrder : Number.MAX_SAFE_INTEGER,
    item: catalogueItem,
  };
}

/** The menu body as sections, in `display_order`. Anything unreadable is `[]`. */
export function catalogueFromPublicMenu(body: unknown, language: string, clock: MenuClock = {}): CatalogueSection[] {
  const items = record(record(body)?.["data"])?.["items"];
  if (!Array.isArray(items)) {
    return [];
  }
  const sections = new Map<string, { label: string; order: number; first: number; items: CatalogueItem[] }>();
  items.forEach((raw, index) => {
    const placed = placeItem(raw, language, clock);
    if (placed === null) {
      return;
    }
    const section = sections.get(placed.sectionId);
    if (section === undefined) {
      sections.set(placed.sectionId, { label: placed.label, order: placed.order, first: index, items: [placed.item] });
    } else {
      section.items.push(placed.item);
    }
  });
  return [...sections.entries()]
    .sort(([, a], [, b]) => a.order - b.order || a.first - b.first)
    .map(([id, section]) => ({ id, label: section.label, items: section.items }));
}
